import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw, ArrowLeft, Music2, Brain, Volume2, VolumeX, Users } from 'lucide-react';
import { buildTable, sayEquation, TABLE_THEMES } from '../lib/tabuada';
import { VOICES, getVoiceById, speak, stopSpeaking, unlockSpeech } from '../lib/speech';
import { playBlip, playCelebrate, unlockAudio } from '../lib/sound';
import { getClipURL } from '../lib/voiceRecordings';
import { Mascot } from '../components/game/Mascot';
import { Confetti } from '../components/game/Confetti';

const SPEEDS = [
  { id: 'slow', label: 'Devagar', mult: 0.8 },
  { id: 'normal', label: 'Normal', mult: 1 },
  { id: 'fun', label: 'Rápido', mult: 1.25 },
];

// Cores vibrantes para os números (visual da tabuada cantada)
const NUM_COLORS = ['#DB2777', '#EA580C', '#CA8A04', '#16A34A', '#0891B2', '#2563EB', '#7C3AED', '#DC2626', '#0D9488', '#E11D48'];

export default function LearnScreen({
  table,
  name,
  voiceId,
  volume,
  onVoiceChange,
  onVolumeChange,
  onAnswer,
  onBack,
}) {
  const rows = buildTable(table);
  const th = TABLE_THEMES[table];
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState('normal');
  const [done, setDone] = useState(false);
  const [showVoices, setShowVoices] = useState(false);
  const playingRef = useRef(true);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  playingRef.current = playing;

  const speedMult = SPEEDS.find((s) => s.id === speed).mult;

  useEffect(() => {
    if (!playing || done) return;
    if (index >= rows.length) {
      setPlaying(false);
      setDone(true);
      playCelebrate();
      speak(`Muito bem, ${name}! Você aprendeu a tabuada do ${table}!`, { voiceId, volume });
      return;
    }
    const q = rows[index];
    playBlip(index);
    const base = getVoiceById(voiceId).rate;

    let advanced = false;
    let localAudio = null;
    const goNext = () => {
      if (advanced || !playingRef.current) return;
      advanced = true;
      clearTimeout(fallback);
      clearTimeout(timerRef.current);
      setIndex((i) => i + 1);
    };
    const afterVoice = () => {
      if (!playingRef.current) return;
      clearTimeout(fallback);
      timerRef.current = setTimeout(goNext, 320);
    };

    // Safety net: some mobile browsers (notably iOS Safari) never fire the
    // speech `onEnd` event, which would freeze the lesson. Always advance
    // after a max duration even if `onEnd` never arrives. For the child's own
    // recordings we allow more time so longer clips are not cut off.
    const fallback = setTimeout(goNext, voiceId === 'mine' ? 7000 : Math.round(3800 / speedMult));

    if (voiceId === 'mine') {
      // Toca a gravação REAL da criança para esta linha (voz própria).
      (async () => {
        const url = await getClipURL(table, index + 1);
        if (!playingRef.current || advanced) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          const audio = new Audio(url);
          localAudio = audio;
          audioRef.current = audio;
          audio.volume = volume === 0 ? 0 : volume;
          audio.onended = () => {
            URL.revokeObjectURL(url);
            afterVoice();
          };
          audio.play().catch(() => {});
        } else {
          // Linha ainda não gravada → usa uma voz padrão como reserva.
          speak(sayEquation(q.a, q.b, q.result), {
            voiceId: 'girl_2',
            volume,
            rate: base * speedMult,
            onEnd: afterVoice,
          });
        }
      })();
    } else {
      speak(sayEquation(q.a, q.b, q.result), {
        voiceId,
        volume,
        rate: base * speedMult,
        onEnd: afterVoice,
      });
    }
    return () => {
      stopSpeaking();
      if (localAudio) localAudio.pause();
      clearTimeout(fallback);
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, playing, speed, voiceId, volume]);

  useEffect(
    () => () => {
      stopSpeaking();
      if (audioRef.current) audioRef.current.pause();
    },
    []
  );

  const togglePlay = () => {
    unlockAudio();
    unlockSpeech();
    if (done) {
      setDone(false);
      setIndex(0);
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  };

  const restart = () => {
    unlockAudio();
    stopSpeaking();
    clearTimeout(timerRef.current);
    setDone(false);
    setIndex(0);
    setPlaying(true);
  };

  const current = Math.min(index, rows.length - 1);
  const progress = Math.min(index, rows.length);

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-2xl flex-col px-4 py-3">
      <div className="mb-3 flex items-center justify-between">
        <button
          data-testid="learn-back-btn"
          onClick={onBack}
          className="btn-3d bg-white px-3 py-2 text-purple-700"
          style={{ borderColor: '#e2e8f0' }}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-fred text-2xl font-bold text-indigo-900 sm:text-3xl">
          Tabuada do <span style={{ color: th.border }}>{table}</span>
        </h1>
        <div className="w-11" />
      </div>

      {/* progress bar */}
      <div className="mb-3">
        <div className="mb-1 flex justify-between text-xs font-black text-purple-600">
          <span>
            <Music2 className="mr-1 inline h-4 w-4" /> Aprendendo cantando
          </span>
          <span data-testid="learn-progress">{progress} de {rows.length}</span>
        </div>
        <div className="h-4 w-full overflow-hidden rounded-full bg-white/70">
          <motion.div
            className="h-full rounded-full"
            style={{ background: th.bg }}
            animate={{ width: `${(progress / rows.length) * 100}%` }}
            transition={{ ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* equation list — a tabuada inteira sempre visível na tela */}
      <div className="card-soft mb-3 flex flex-1 flex-col justify-between p-2 sm:p-3">
        {rows.map((q, i) => {
          const isCur = i === current && !done;
          const aColor = i % 2 === 0 ? '#7C3AED' : '#2563EB';
          const bColor = NUM_COLORS[i % NUM_COLORS.length];
          const rColor = NUM_COLORS[(i + 4) % NUM_COLORS.length];
          return (
            <motion.div
              key={i}
              data-testid={`learn-row-${i + 1}`}
              animate={isCur ? { scale: 1.05 } : { scale: 1 }}
              className="flex items-center justify-center gap-2 rounded-2xl px-2 py-1 font-fred text-2xl font-black sm:gap-3 sm:text-3xl"
              style={{
                background: isCur ? '#FEF08A' : 'transparent',
                border: isCur ? '3px solid #F59E0B' : '3px solid transparent',
                boxShadow: isCur ? '0 6px 16px rgba(245,158,11,0.35)' : 'none',
              }}
            >
              <span style={{ color: aColor }}>{q.a}</span>
              <span className="text-slate-400">×</span>
              <span style={{ color: bColor }}>{q.b}</span>
              <span className="text-slate-400">=</span>
              <motion.span
                key={isCur ? `hot-${i}` : `cold-${i}`}
                animate={isCur ? { scale: [1, 1.25, 1] } : {}}
                transition={{ duration: 0.5 }}
                style={{ color: rColor }}
              >
                {q.result}
              </motion.span>
            </motion.div>
          );
        })}
      </div>

      {/* voice switcher */}
      <AnimatePresence>
        {showVoices && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-3 overflow-hidden"
          >
            <div className="card-soft grid grid-cols-6 gap-2 p-3">
              {VOICES.map((v) => (
                <button
                  key={v.id}
                  data-testid={`learn-voice-${v.id}`}
                  onClick={() => {
                    onVoiceChange(v.id);
                    setShowVoices(false);
                    speak(`Oi! Agora sou ${v.label}!`, { voiceId: v.id, volume });
                  }}
                  className={`rounded-xl px-1 py-2 text-xs font-black ${
                    voiceId === v.id ? 'bg-emerald-400 text-white' : 'bg-white text-purple-700'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* controls */}
      <div className="flex items-center justify-center gap-3">
        <button
          data-testid="learn-restart-btn"
          onClick={restart}
          className="btn-3d h-14 w-14 rounded-full bg-white p-0 text-purple-600"
          style={{ borderColor: '#e2e8f0' }}
          title="Recomeçar"
        >
          <RotateCcw className="h-6 w-6" />
        </button>
        <button
          data-testid="learn-play-btn"
          onClick={togglePlay}
          className="btn-3d h-20 w-20 rounded-full p-0 text-white"
          style={{ background: th.bg, borderColor: th.border }}
        >
          {playing && !done ? <Pause className="h-9 w-9" /> : <Play className="ml-1 h-9 w-9" />}
        </button>
        <button
          data-testid="learn-voice-toggle"
          onClick={() => setShowVoices((s) => !s)}
          className="btn-3d h-14 w-14 rounded-full bg-white p-0 text-pink-600"
          style={{ borderColor: '#e2e8f0' }}
          title="Trocar voz"
        >
          <Users className="h-6 w-6" />
        </button>
      </div>

      {/* speed + volume */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <span className="text-xs font-black text-purple-500">Velocidade:</span>
        {SPEEDS.map((s) => (
          <button
            key={s.id}
            data-testid={`speed-${s.id}`}
            onClick={() => setSpeed(s.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-black ${
              speed === s.id ? 'bg-purple-500 text-white' : 'bg-white text-purple-600'
            }`}
          >
            {s.label}
          </button>
        ))}
        <button
          data-testid="learn-volume-btn"
          onClick={() => onVolumeChange && onVolumeChange(volume === 0 ? 1 : 0)}
          className={`ml-1 flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-black ${
            volume === 0 ? 'bg-rose-100 text-rose-500' : 'bg-emerald-100 text-emerald-600'
          }`}
          title="Volume"
        >
          {volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          {volume === 0 ? 'Mudo' : 'Som'}
        </button>
      </div>

      {/* Done celebration overlay */}
      <AnimatePresence>
        {done && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-purple-900/40 p-5 backdrop-blur-sm"
          >
            <Confetti show={done} />
            <motion.div
              initial={{ scale: 0.6, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              className="card-soft w-full max-w-md p-6 text-center"
            >
              <div className="mx-auto -mt-20 mb-2 w-fit">
                <Mascot mood="cheer" size={140} />
              </div>
              <h2 className="font-fred text-3xl font-bold text-emerald-600">🎉 Muito bem, {name}!</h2>
              <p className="mt-1 mb-5 text-lg font-bold text-purple-700">
                Você acabou de aprender a tabuada do {table}!
              </p>
              <div className="flex flex-col gap-3">
                <button
                  data-testid="learn-replay-btn"
                  onClick={restart}
                  className="btn-3d w-full bg-sky-400 px-5 py-3 text-white"
                  style={{ borderColor: '#0284C7' }}
                >
                  <RotateCcw className="h-5 w-5" /> Ouvir novamente
                </button>
                <button
                  data-testid="go-answer-btn"
                  onClick={() => {
                    unlockSpeech();
                    onAnswer(table);
                  }}
                  className="btn-3d w-full bg-orange-500 px-5 py-3 text-white"
                  style={{ borderColor: '#EA580C' }}
                >
                  <Brain className="h-5 w-5" /> Quero responder
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
